<template>
  <div class="site-shell">
    <header class="site-header">
      <a class="brand" href="/index" @click.prevent="navigate('/index')">
        <img class="brand-logo" :src="logoMark" :alt="appName" />
        <span class="brand-name">{{ appName }}</span>
      </a>

      <nav class="desktop-nav" aria-label="Primary navigation">
        <a
          v-for="item in navItems"
          :key="item.path"
          :class="{ active: activePage === item.page }"
          :href="item.path"
          @click.prevent="navigate(item.path)"
        >
          {{ item.label }}
        </a>
      </nav>

      <button class="menu-button" type="button" aria-label="Open navigation" @click="menuOpen = !menuOpen">
        <span></span>
        <span></span>
        <span></span>
      </button>

      <nav v-if="menuOpen" class="mobile-nav" aria-label="Mobile navigation">
        <a
          v-for="item in navItems"
          :key="item.path"
          :class="{ active: activePage === item.page }"
          :href="item.path"
          @click.prevent="navigate(item.path)"
        >
          {{ item.label }}
        </a>
      </nav>
    </header>

    <main v-if="activePage === 'home'" class="home-page">
      <section class="hero">
        <img class="hero-image" :src="heroImage" :alt="`${appName} app preview`" />
        <div class="hero-copy">
          <img class="hero-logo" :src="logoMark" :alt="appName" />
          <p class="eyebrow">Voice party rooms, chat, moments, and games.</p>
          <h1>{{ appName }}</h1>
          <p class="hero-text">
            Hang out in live voice party rooms, send virtual gifts, chat one-to-one, share photo moments,
            play Ludo, Baloot and Estimation, and ask the AI assistant for ideas.
          </p>
          <div class="hero-actions">
            <a class="primary-action" :href="googlePlayUrl" target="_blank" rel="noopener">
              <span class="action-icon play-icon"></span>
              Get it on Google Play
            </a>
            <a class="secondary-action" :href="appStoreUrl" target="_blank" rel="noopener">
              <span class="action-icon phone-icon"></span>
              Download on the App Store
            </a>
          </div>
          <p v-if="androidRelease" class="download-meta">
            Direct APK:
            <a :href="androidDownloadUrl" @click="trackApkDownload">v{{ androidRelease.versionName }}</a>
            <span v-if="androidRelease.fileSizeText"> · {{ androidRelease.fileSizeText }}</span>
            <span v-if="androidRelease.releaseTime"> · Updated {{ String(androidRelease.releaseTime).slice(0, 10) }} (UTC+3)</span>
          </p>
        </div>
      </section>

      <section class="section intro-section" aria-labelledby="intro-title">
        <div class="section-inner two-column">
          <div>
            <p class="section-kicker">What {{ appName }} Does</p>
            <h2 id="intro-title">A social voice party app.</h2>
          </div>
          <p class="section-lede">
            {{ appName }} brings live audio voice rooms, private chat, photo moments, following, free offline casual
            games and an AI assistant into one app. Coins are an optional virtual currency bought in the app through
            the App Store or Google Play and used for gifts, dress-up items, paid rooms, and AI replies. Coins have no
            cash value.
          </p>
        </div>
      </section>

      <section class="section feature-section" :aria-label="`${appName} features`">
        <div class="section-inner feature-grid">
          <article v-for="feature in features" :key="feature.title" class="feature-card">
            <span class="feature-icon" :class="feature.iconClass"></span>
            <h3>{{ feature.title }}</h3>
            <p>{{ feature.text }}</p>
          </article>
        </div>
      </section>

      <section class="section store-section" aria-labelledby="store-title">
        <div class="section-inner">
          <div class="section-heading">
            <p class="section-kicker">Store Preview</p>
            <h2 id="store-title">Available on Google Play and the App Store.</h2>
            <p>
              The two preview visuals below were generated with AI from {{ appName }}'s screenshot style and then saved as
              lightweight project assets for the official website.
            </p>
          </div>
          <div class="store-grid">
            <article v-for="preview in storePreviews" :key="preview.title" class="store-card">
              <img :src="preview.image" :alt="preview.alt" />
              <div>
                <p>{{ preview.platform }}</p>
                <h3>{{ preview.title }}</h3>
                <a :href="preview.url" class="store-download" target="_blank" rel="noopener">
                  {{ preview.button }}
                </a>
              </div>
            </article>
          </div>
        </div>
      </section>

      <section class="section screenshot-section" aria-labelledby="screens-title">
        <div class="section-inner">
          <div class="section-heading">
            <p class="section-kicker">Inside The App</p>
            <h2 id="screens-title">Screenshots from the {{ appName }} app.</h2>
          </div>
          <div class="phone-gallery">
            <article v-for="screen in screenshots" :key="screen.title" class="phone-shot">
              <img :src="screen.image" :alt="screen.alt" />
              <h3>{{ screen.title }}</h3>
              <p>{{ screen.text }}</p>
            </article>
          </div>
        </div>
      </section>

      <section class="section experience-section" aria-labelledby="experience-title">
        <div class="section-inner">
          <p class="section-kicker">Designed For Daily Use</p>
          <h2 id="experience-title">Talk, share, and play with friends.</h2>
          <div class="experience-list">
            <article v-for="step in experience" :key="step.title" class="experience-item">
              <span>{{ step.number }}</span>
              <div>
                <h3>{{ step.title }}</h3>
                <p>{{ step.text }}</p>
              </div>
            </article>
          </div>
        </div>
      </section>

      <section id="support" class="section intro-section" aria-labelledby="support-title">
        <div class="section-inner two-column">
          <div>
            <p class="section-kicker">Support</p>
            <h2 id="support-title">Contact {{ appName }} support.</h2>
          </div>
          <div class="section-lede">
            <p>
              Questions about your account, purchases, reports or safety? Email customer support at
              <a :href="`mailto:${company.supportEmail}`">{{ company.supportEmail }}</a>. For privacy requests and
              account deletion, email <a :href="`mailto:${company.email}`">{{ company.email }}</a>. Please include
              your {{ appName }} User ID. We reply within 3 business days.
            </p>
            <p>In the app you can also use Me &gt; Settings &gt; Contact Us or Feedback.</p>
          </div>
        </div>
      </section>
    </main>

    <RechargePage v-else-if="activePage === 'recharge'" />

    <main v-else class="document-page">
      <section class="document-shell">
        <button class="back-button" type="button" @click="navigate('/index')">
          <span class="back-icon"></span>
          Back Home
        </button>
        <p class="document-kicker">{{ appName }}</p>
        <h1>{{ pageConfig.title }}</h1>
        <p class="updated">{{ pageConfig.updated }}</p>
        <p v-if="pageConfig.intro" class="document-intro">{{ pageConfig.intro }}</p>

        <section v-for="section in pageConfig.sections" :key="section.title" class="legal-section">
          <h2>{{ section.title }}</h2>
          <p v-for="paragraph in section.paragraphs" :key="paragraph">{{ paragraph }}</p>
        </section>

        <section class="contact-details" aria-label="Company contact details">
          <h2>Contact Details</h2>
          <p>Customer support: <a :href="`mailto:${company.supportEmail}`">{{ company.supportEmail }}</a></p>
          <p>Privacy, legal and account deletion: <a :href="`mailto:${company.email}`">{{ company.email }}</a></p>
          <p v-for="entity in company.entities" :key="entity.name">
            Company ({{ entity.region }}): {{ entity.name }}<template v-if="entity.address">, {{ entity.address }}</template>
          </p>
          <p>{{ company.statement }}</p>
        </section>
      </section>
    </main>

    <footer class="site-footer">
      <div class="footer-inner">
        <div>
          <div class="footer-brand">
            <img class="footer-logo" :src="logoMark" :alt="appName" />
            <span class="footer-name">{{ appName }}</span>
          </div>
          <p v-for="entity in company.entities" :key="entity.name">{{ entity.name }} ({{ entity.region }})</p>
          <p>{{ company.statement }}</p>
        </div>
        <div class="footer-links">
          <a href="/recharge-pages/" @click.prevent="navigate('/recharge-pages/')">Recharge</a>
          <a href="/Terms" @click.prevent="navigate('/Terms')">Terms</a>
          <a href="/Privacy" @click.prevent="navigate('/Privacy')">Privacy</a>
          <a href="/DataDeletion" @click.prevent="navigate('/DataDeletion')">Data Deletion</a>
          <a href="/About" @click.prevent="navigate('/About')">About Us</a>
          <a :href="`mailto:${company.supportEmail}`">Support: {{ company.supportEmail }}</a>
          <a :href="`mailto:${company.email}`">{{ company.email }}</a>
        </div>
      </div>
    </footer>

    <div v-if="popup.visible" class="modal-overlay" @click="closePopup">
      <div class="modal" role="dialog" aria-modal="true" aria-labelledby="modal-title" @click.stop>
        <h2 id="modal-title">{{ popup.title }}</h2>
        <p>{{ popup.message }}</p>
        <button type="button" @click="closePopup">Confirm</button>
      </div>
    </div>
  </div>
</template>

<script>
import logoMark from './assets/sawa-party-logo.webp'
import { apkDownloadUrl, getLatestApk } from './api/app-release'
import { getStoreLinks } from './api/app-config'
import heroImage from './assets/store/google-play-preview.webp'
import googlePreview from './assets/store/google-play-preview.webp'
import appStorePreview from './assets/store/app-store-preview.webp'
import voiceRoomsScreen from './assets/app-screenshots-1.0.4/voice-rooms.webp'
import voiceRoomScreen from './assets/app-screenshots-1.0.4/voice-room.webp'
import sendGiftScreen from './assets/app-screenshots-1.0.4/send-gift.webp'
import walletScreen from './assets/app-screenshots-1.0.4/wallet.webp'
import discoverScreen from './assets/app-screenshots-1.0.4/discover.webp'
import messagesScreen from './assets/app-screenshots-1.0.4/messages.webp'
import aiAssistantScreen from './assets/app-screenshots-1.0.4/ai-assistant.webp'
import signInScreen from './assets/app-screenshots-1.0.4/sign-in.webp'
import RechargePage from './components/RechargePage.vue'
import { APP_NAME } from './config/app'

// Fallbacks only: the live links come from the Admin App main configuration (see loadStoreLinks).
const GOOGLE_PLAY_URL = 'https://play.google.com/store/apps/details?id=com.xianxinghouzhi.rayaapp'
const APP_STORE_URL = 'https://apps.apple.com/us/app/raya-live/id6789512397'

// One company operating through two regional entities.
const COMPANY_ENTITIES = [
  {
    name: 'PROBEKNOW NETWORK LIMITED',
    region: 'Hong Kong'
  },
  {
    name: 'Guangzhou Xianxing Houzhi Network Technology Co., Ltd.',
    region: 'Mainland China',
    address: 'Room 401-G115, Building 1, No. 6 Yingxin Road, Tianhe District, Guangzhou'
  }
]

const COMPANY = {
  // Privacy, legal and account deletion requests.
  email: 'xianxinghouzhi@gmail.com',
  // Customer support: the address on the app's Contact Us page.
  supportEmail: 'shenmeniuma@126.com',
  name: COMPANY_ENTITIES.map(entity => entity.name).join(' / '),
  entities: COMPANY_ENTITIES,
  statement: `${COMPANY_ENTITIES.map(entity => `${entity.name} (${entity.region})`).join(' and ')} are the same company operating in different regions.`
}

const EFFECTIVE_DATE = 'October 30, 2026'

const TERMS_SECTIONS = [
  {
    title: '1. Acceptance and Eligibility',
    paragraphs: [
      `These Terms of Service are an agreement between you and the operator of ${APP_NAME} (${COMPANY.name}). By registering, signing in (including as a guest) or otherwise using ${APP_NAME}, including the mobile app, this website and related services, you agree to these terms, our Privacy Policy and our community rules. If you do not agree, do not use ${APP_NAME}.`,
      `You must be at least 18 years old, or the age of majority where you live if higher, to use ${APP_NAME}. The service is not directed to children.`,
      'We may update these terms. The date at the top shows the latest version, and material changes are announced in the app.'
    ]
  },
  {
    title: `2. What ${APP_NAME} Offers`,
    paragraphs: [
      `${APP_NAME} is a social voice party app. It offers live audio-only voice rooms (mic seats, room chat, virtual gifts, room rankings, music played by hosts, and public, password, invite-only and paid-entry rooms), one-to-one private messaging, photo moments with likes and comments, following, virtual coins, gifts, dress-up items and VIP, and free offline casual games (Ludo, Baloot and Estimation, played against computer opponents with no coins, prizes or anything of value).`,
      'It also includes an AI assistant whose replies are generated by OpenAI after you agree in the app, and pet community content (pet profiles, pet walk events and the Dog MBTI quiz). AI replies may be inaccurate and are not professional advice.',
      'Hosts are responsible for holding the rights to any music they play in a voice room.'
    ]
  },
  {
    title: '3. Accounts',
    paragraphs: [
      'You can sign in as a guest, with a phone number and SMS code, with email, with your account ID and password, or with Sign in with Apple (and Google Sign-In on Android). A guest account that is not linked to a phone number or email cannot be recovered after you sign out or remove the app.',
      'You are responsible for keeping your credentials secure and for all activity under your account. The information you provide must be true, accurate and current.'
    ]
  },
  {
    title: '4. User Content, Community Rules and Moderation',
    paragraphs: [
      `You are responsible for the voice, text, photos, posts, comments, messages, AI prompts and other materials you share through ${APP_NAME}, and you must have the rights to share them.`,
      `We have zero tolerance for objectionable content and abusive users. You must not post, stream or send content that is illegal, sexually explicit, violent, hateful, harassing, threatening, defamatory, fraudulent, infringing or privacy-violating, or that promotes self-harm, drugs, weapons, gambling or other dangerous activity. You must not impersonate others, harass or scam users, trade accounts or virtual items for money, collect personal information without permission, interfere with the service, or access systems or accounts without authorization.`,
      `You can report a user, room, post or message from the report option in the app, or by emailing ${COMPANY.supportEmail}. You can block any user from their profile; blocked users can no longer message or interact with you, and you can manage your blocked list in Settings.`,
      'We review reports and act on valid reports within 24 hours by removing the content and, where appropriate, warning, restricting, suspending or permanently banning the account. We may also review and remove content proactively and report illegal content to the authorities.'
    ]
  },
  {
    title: '5. Child Safety',
    paragraphs: [
      `${APP_NAME} strictly prohibits child sexual abuse and exploitation, grooming, trafficking, predatory behavior, and any content or activity that exploits or harms minors.`,
      'Violations result in permanent account termination and reporting to the relevant authorities or child safety organizations.'
    ]
  },
  {
    title: '6. Virtual Coins, Gifts, VIP and Payments',
    paragraphs: [
      `Coins are ${APP_NAME}'s virtual currency. In the iOS app, coins are sold only as consumable Apple In-App Purchases, and in the Android app through Google Play Billing. You must be 18 or older to buy coins. Free coins are also available from the daily check-in and a first-recharge bonus.`,
      'Coins can be spent on virtual gifts, entry to paid rooms, time-limited dress-up items (entry vehicles, avatar frames, chat bubbles, profile effects, sound waves), and AI assistant replies (100 coins per successful reply). Diamonds are received from gifts and can only be exchanged into coins in the app. There are no random or chance-based rewards for coins.',
      'VIP is a set of time-limited cosmetic privileges unlocked automatically when your coin purchases in a VIP period reach the level shown on the in-app VIP page. It is not sold separately and is not a subscription.',
      'Coins, diamonds, gifts, dress-up items and VIP have no monetary value and cannot be withdrawn, cashed out, transferred outside the app, or exchanged for money. All purchases are final and non-refundable, except where required by law or under the store\'s refund policy. Refunds for App Store purchases are handled by Apple at reportaproblem.apple.com. Unused coins and items are forfeited when an account is deleted or terminated for breaking these terms.'
    ]
  },
  {
    title: '7. Intellectual Property',
    paragraphs: [
      `${APP_NAME}, including its software, designs, graphics, interfaces, logos, and service content, is owned by or licensed to us and is protected by intellectual property laws.`,
      `You retain rights in your own user content, but you grant ${APP_NAME} a worldwide, non-exclusive, royalty-free license to host, store, reproduce, display, adapt, and distribute that content as needed to operate, improve, promote, and protect the service.`
    ]
  },
  {
    title: '8. Privacy',
    paragraphs: [
      `Your use of ${APP_NAME} is also governed by our Privacy Policy, which explains what information we collect, how we use and share it, how long we keep it, and how to delete it.`
    ]
  },
  {
    title: '9. Suspension, Termination and Account Deletion',
    paragraphs: [
      `We may suspend or terminate access to ${APP_NAME} if you violate these terms, create risk for other users, expose us to legal risk, or misuse the service.`,
      `You may stop using ${APP_NAME} at any time. You can delete your account in the app under Settings > Delete Account, or by emailing ${COMPANY.email}. The User Data Deletion page explains what is deleted and what we must keep.`
    ]
  },
  {
    title: '10. Disclaimers and Liability',
    paragraphs: [
      `${APP_NAME} is provided on an "as is" and "as available" basis. To the fullest extent permitted by law, we do not guarantee that the service will be uninterrupted, error-free, or secure.`,
      'We are not responsible for user content, the conduct of other users, AI replies, third-party services, network conditions, device issues, or events outside our reasonable control. Nothing in these terms limits rights you have under consumer protection law that cannot be limited by contract.'
    ]
  },
  {
    title: '11. Apple App Store',
    paragraphs: [
      'If you downloaded the app from the Apple App Store, these terms are between you and us, not Apple. Apple is not responsible for the app, its content, its maintenance or support, or any claims relating to it, and Apple and its subsidiaries are third-party beneficiaries of these terms. You must also comply with the Apple Media Services Terms and Conditions.'
    ]
  },
  {
    title: '12. Governing Law',
    paragraphs: [
      'These terms are governed by the laws of Singapore. Any dispute shall be resolved by arbitration administered by the Singapore International Arbitration Centre, seated in Singapore and conducted in English.'
    ]
  },
  {
    title: '13. Contact',
    paragraphs: [
      `Questions about these terms: ${COMPANY.email}. Customer support: ${COMPANY.supportEmail}.`
    ]
  }
]

const PRIVACY_SECTIONS = [
  {
    title: '1. Information We Collect',
    paragraphs: [
      'All information below is linked to your account and is used only to provide the app\'s features. None of it is used to track you across other companies\' apps or websites.',
      'Account and contact information: phone number (phone and SMS code sign-in), email address (email sign-in or linking), the name and email that Sign in with Apple or Google Sign-In shares with us, your password if you set one, and the user ID and account ID we assign. Guest accounts are created from an app-generated device identifier.',
      'Profile and content: nickname, avatar, bio, gender and birthday if you add them, pet profiles, dress-up items, photos, posts, place names you type, comments, likes, private and room chat messages, room titles, covers and announcements, feedback, reports and customer support messages, and messages you send to the AI assistant.',
      'Purchases: purchase history with the store transaction ID and product ID, coin and diamond balances, gifts sent and received, room entry fees, dress-up purchases, VIP level and AI reply charges. Payments are processed by Apple or Google; we never receive card details.',
      'Usage and device: product interaction such as rooms joined, mic sessions, follows, likes, games opened, reports and blocks; and an app-generated device identifier, platform, app version and language sent with each request to keep you signed in and protect your account.',
      `${APP_NAME} does not use the advertising identifier, has no advertising or third-party analytics SDK, and does not collect your device location, contacts, health or biometric data.`
    ]
  },
  {
    title: '2. Microphone, Camera, and Photo Permissions',
    paragraphs: [
      'Microphone: only to let you speak when you take a mic seat in a voice room. Your voice is streamed in real time to the people in that room through Tencent Cloud Real-Time Communication and is not recorded by us.',
      'Camera: only when you choose to take a photo, such as a profile photo. Photo library: only when you choose an image for your avatar, a post, a chat message, or a room cover or background.',
      'Each permission is requested when you first use the related feature. You can withdraw it in your device settings; only the related feature then stops working.'
    ]
  },
  {
    title: '3. AI Assistant and Your Consent',
    paragraphs: [
      'AI assistant replies are generated by OpenAI, a third-party AI provider. Before your first AI message the app asks for your consent; only after you agree is the text of your AI conversation (no name, phone number, or account details) sent to OpenAI through our AI proxy service to generate a reply.',
      `You can withdraw consent by no longer using the AI assistant; reinstalling the app resets the consent prompt, and you can ask us at ${COMPANY.email} to delete your stored AI conversations. Do not include personal information in AI messages.`
    ]
  },
  {
    title: '4. How We Use Information',
    paragraphs: [
      `We use information to provide ${APP_NAME}'s features (voice rooms, messaging, moments, following, games, VIP, AI assistant, virtual coins, and account features), credit purchases, handle support requests, review reports and moderate content, prevent fraud, and comply with law.`,
      'We do not sell your personal information and do not use it for advertising.'
    ]
  },
  {
    title: '5. Third Parties That Process Information',
    paragraphs: [
      'We share only what each service provider needs: Tencent Cloud (Chat for private and room messages, and Real-Time Communication for live voice, including the call-quality and diagnostic data these SDKs collect to keep them working), Alibaba Cloud Object Storage Service (uploaded photos), OpenAI (AI conversation text, only after your consent), Apple (Sign in with Apple and In-App Purchase), and Google (Google Sign-In and Google Play Billing on Android, and Google Fonts).',
      'We may disclose information if required by law, legal process, safety needs, rights protection, or a business transfer. Your nickname, avatar, profile, VIP level, posts, room messages, voice on a mic seat, gifts, followers and ranking positions are visible to other users. Private messages are visible only to you and the recipient, except where reviewed after a report.'
    ]
  },
  {
    title: '6. Data Retention',
    paragraphs: [
      'Account, profile, content, messages and AI conversations are kept while your account exists; content you delete is removed from active systems promptly.',
      'Purchase and transaction records are kept for as long as accounting, tax and anti-fraud laws require (generally up to 5 years). Records about reported abuse or illegal activity are kept as long as needed to protect users and meet legal obligations. Residual backup copies are removed within 30 days.'
    ]
  },
  {
    title: '7. Account and Data Deletion',
    paragraphs: [
      `You can delete your account in the app under Settings > Delete Account, or by emailing ${COMPANY.email} with your User ID. We complete verified requests within 15 days.`,
      'When your account is deleted, your profile, photos, posts, comments, messages, AI conversations, follow relationships and device identifier are deleted or anonymized, except records we must keep as described above. The User Data Deletion page on this website explains each step.'
    ]
  },
  {
    title: '8. Your Rights and Choices',
    paragraphs: [
      'Depending on your location, you may have rights to access, correct, delete, restrict, object to, or request a copy of your personal information. You can edit your profile, delete posts and comments, block users, and change permissions in the app or device settings.',
      `To make a privacy request, email ${COMPANY.email}. We respond to verified requests within 15 days.`
    ]
  },
  {
    title: '9. Security',
    paragraphs: [
      'We use reasonable technical and organizational safeguards designed to protect your information from unauthorized access, loss, misuse, alteration, or disclosure.',
      'No online service can guarantee absolute security, so you should use a strong password and keep your account information confidential.'
    ]
  },
  {
    title: '10. Children',
    paragraphs: [
      `${APP_NAME} is only for users aged 18 and over. We do not knowingly collect personal information from anyone under 18. If we learn that a minor has created an account, we will delete the account and its data.`
    ]
  },
  {
    title: '11. International Transfers',
    paragraphs: [
      'We and our service providers may store and process information on servers located outside your country. We protect transferred information as described in this policy.'
    ]
  },
  {
    title: '12. Updates to This Policy',
    paragraphs: [
      'We may update this Privacy Policy when our practices or features change. The date at the top shows the latest version, and significant changes are announced in the app.'
    ]
  },
  {
    title: '13. Contact Us',
    paragraphs: [
      `Privacy and deletion requests: ${COMPANY.email}. Customer support: ${COMPANY.supportEmail}. Operator: ${COMPANY.name}.`
    ]
  }
]

const DATA_DELETION_SECTIONS = [
  {
    title: '1. Delete Your Account and Data in the App',
    paragraphs: [
      `The fastest way to delete your data is from inside the app. Open ${APP_NAME}, go to Settings, choose Delete Account, review the deletion agreement, and follow the on-screen prompts to confirm.`,
      'Your account must be in a normal, secure state before the request can be processed: it must not be blocked, frozen, or restricted for policy violations; it must not be under review for reports or complaints; and it must have no active orders, unsettled balances, or open support tickets.',
      'Once you confirm, the deletion request is submitted immediately and the account is closed. The request cannot be cancelled afterwards.'
    ]
  },
  {
    title: '2. Request Deletion by Email',
    paragraphs: [
      `If you have uninstalled the app or cannot sign in, you can still request deletion by emailing ${COMPANY.email} with the subject "Account and Data Deletion".`,
      `Include the registered ${APP_NAME} User ID, plus the phone number or email address used to sign up, so that we can verify that the request comes from the account owner. We may ask for additional verification before acting on a request.`,
      'We confirm receipt and complete verified requests within 15 days.'
    ]
  },
  {
    title: '3. Data That Is Deleted or Anonymized',
    paragraphs: [
      'Profile data: nickname, avatar, bio, gender, birthday, pet profiles, and other details you added to your account.',
      'Content you created: moments, comments, photos, rooms you created, and AI assistant prompts and responses.',
      'Social and activity data: followers and following lists, friend relationships, chat messages, walk and route history, and in-app interaction records.',
      'Device and technical data linked to your account: the app-generated device identifier and request logs tied to your user ID.'
    ]
  },
  {
    title: '4. Data We Keep and For How Long',
    paragraphs: [
      'Transaction and payment records (including coin purchases, refunds, and invoices) are kept for as long as accounting, tax, and anti-fraud laws require. In China this is generally up to 5 years from the transaction date.',
      'Safety and moderation records for accounts involved in reported abuse, fraud, or illegal activity are kept for as long as needed to protect other users and to meet legal obligations.',
      'Aggregated or anonymized statistics that can no longer identify you may be retained indefinitely. They are not linked to you or your device.',
      'Residual copies in encrypted backups are removed on the normal backup rotation, within 30 days of deletion at the latest. Once a legal retention period expires, the remaining records are purged as well.'
    ]
  },
  {
    title: '5. Before You Delete',
    paragraphs: [
      'Account deletion is permanent and irreversible. You cannot reactivate the account, sign in again with the same credentials, or recover any content afterwards.',
      'Unused coins, diamonds, gifts, dress-up items, VIP, and other virtual items are forfeited when the account is deleted. They are non-refundable and cannot be transferred to another account.',
      'A guest account that is not linked to a phone number or email cannot be recovered once deleted.',
      `${APP_NAME} does not sell subscriptions, so there is no store subscription to cancel. Coin purchases already made are not refunded when you delete your account.`
    ]
  },
  {
    title: '6. Deleting Some Data Without Deleting Your Account',
    paragraphs: [
      'You can delete individual posts, comments, photos, and pet profiles at any time from within the app without closing your account.',
      `You can withdraw camera, photo, and microphone permissions in your device settings. ${APP_NAME} then stops collecting that data, although the related features may no longer work.`,
      `To request access to, correction of, or a copy of your personal information instead of deletion, contact us at ${COMPANY.email}.`
    ]
  },
  {
    title: '7. Contact',
    paragraphs: [
      `For any question about data deletion, contact ${COMPANY.name} at ${COMPANY.email}. See also our Privacy Policy for the full description of how we collect, use, and protect information.`
    ]
  }
]

const ABOUT_SECTIONS = [
  {
    title: `About ${APP_NAME}`,
    paragraphs: [
      `${APP_NAME} is a social voice party app: live audio voice rooms, one-to-one chat, photo moments, following, free offline casual games (Ludo, Baloot and Estimation), and an AI assistant powered by OpenAI.`,
      'Users can take a mic seat in voice rooms, send virtual gifts with optional coins bought in the app, chat, share moments, play casual games against computer opponents, and explore pet community content such as pet profiles and the Dog MBTI quiz.',
      `${APP_NAME} is for adults aged 18 and over. We keep the community safe with in-app reporting, blocking and content moderation.`
    ]
  },
  {
    title: 'Support and Contact',
    paragraphs: [
      `Customer support: ${COMPANY.supportEmail}`,
      `Privacy, legal and account deletion: ${COMPANY.email}`,
      `Please include your ${APP_NAME} User ID. We reply within 3 business days. In the app you can also use Me > Settings > Contact Us or Feedback.`,
      ...COMPANY.entities.map(entity => `Company (${entity.region}): ${entity.name}${entity.address ? `, ${entity.address}` : ''}`),
      COMPANY.statement
    ]
  }
]

export default {
  name: 'App',
  components: {
    RechargePage
  },
  data() {
    return {
      appName: APP_NAME,
      logoMark,
      heroImage,
      currentPath: '/index',
      menuOpen: false,
      androidRelease: null,
      androidDownloadUrl: apkDownloadUrl(),
      googlePlayUrl: GOOGLE_PLAY_URL,
      appStoreUrl: APP_STORE_URL,
      popup: {
        visible: false,
        title: '',
        message: ''
      },
      company: COMPANY,
      navItems: [
        { label: 'HomePage', path: '/index', page: 'home' },
        { label: 'Recharge', path: '/recharge-pages/', page: 'recharge' },
        { label: 'Terms', path: '/Terms', page: 'terms' },
        { label: 'Privacy', path: '/Privacy', page: 'privacy' },
        { label: 'Data Deletion', path: '/DataDeletion', page: 'dataDeletion' },
        { label: 'About Us', path: '/About', page: 'about' }
      ],
      features: [
        {
          title: 'Live Voice Rooms',
          text: 'Join audio-only rooms, take a mic seat, chat, and create your own public, password, invite-only, or paid room.',
          iconClass: 'icon-route'
        },
        {
          title: 'Coins, Gifts and Dress-Up',
          text: 'Optional coins from in-app purchase or daily check-in, for virtual gifts, dress-up items, paid rooms, and AI replies. No cash value.',
          iconClass: 'icon-paw'
        },
        {
          title: 'Chat, Moments and Games',
          text: 'One-to-one messages, photo moments, free offline Ludo, Baloot and Estimation, and an AI assistant powered by OpenAI.',
          iconClass: 'icon-ai'
        }
      ],
      storePreviews: [
        {
          platform: 'Google Play',
          title: 'Feature graphic preview',
          button: 'Get it on Google Play',
          url: GOOGLE_PLAY_URL,
          image: googlePreview,
          alt: `Google Play style ${APP_NAME} promotional feature graphic`
        },
        {
          platform: 'App Store',
          title: 'App Store screenshot preview',
          button: 'Download on the App Store',
          url: APP_STORE_URL,
          image: appStorePreview,
          alt: `App Store style ${APP_NAME} promotional screenshot`
        }
      ],
      screenshots: [
        {
          title: 'Live voice rooms',
          text: 'Browse audio-only rooms, search by name, or create your own.',
          image: voiceRoomsScreen,
          alt: `${APP_NAME} voice room list app screenshot`
        },
        {
          title: 'On the mic',
          text: 'Take a seat, talk with friends, chat, and send gifts.',
          image: voiceRoomScreen,
          alt: `${APP_NAME} voice room app screenshot`
        },
        {
          title: 'Virtual gifts',
          text: 'Send animated gifts to people on the mic, paid with coins.',
          image: sendGiftScreen,
          alt: `${APP_NAME} send a gift app screenshot`
        },
        {
          title: 'Wallet',
          text: 'Optional coins from Apple In-App Purchase or the daily check-in. No cash value.',
          image: walletScreen,
          alt: `${APP_NAME} wallet app screenshot`
        },
        {
          title: 'Discover',
          text: 'Share photo moments, likes and comments.',
          image: discoverScreen,
          alt: `${APP_NAME} discover feed app screenshot`
        },
        {
          title: 'Messages',
          text: 'One-to-one chat with photos, gifts, and shared rooms.',
          image: messagesScreen,
          alt: `${APP_NAME} messages app screenshot`
        },
        {
          title: 'AI assistant',
          text: 'Replies from OpenAI, only after you agree; 100 coins per reply.',
          image: aiAssistantScreen,
          alt: `${APP_NAME} AI assistant app screenshot`
        },
        {
          title: 'Sign in',
          text: 'Guest, phone, email, account ID, or Sign in with Apple.',
          image: signInScreen,
          alt: `${APP_NAME} sign-in app screenshot`
        }
      ],
      experience: [
        {
          number: '01',
          title: 'Talk',
          text: 'Join a live voice party room, take a mic seat, or create a room for your own community.'
        },
        {
          number: '02',
          title: 'Share',
          text: 'Post photo moments, chat one-to-one, and send virtual gifts to people you meet.'
        },
        {
          number: '03',
          title: 'Play',
          text: 'Play free offline Ludo, Baloot and Estimation, or ask the AI assistant for ideas.'
        }
      ],
      pageMap: {
        terms: {
          title: 'Terms of Service',
          updated: `Last updated: ${EFFECTIVE_DATE}`,
          intro: `These Terms of Service explain the rules for using ${APP_NAME} and the responsibilities that apply to users of our services.`,
          sections: TERMS_SECTIONS
        },
        privacy: {
          title: 'Privacy Policy',
          updated: `Last updated: ${EFFECTIVE_DATE}`,
          intro: `This Privacy Policy explains how ${APP_NAME} collects, uses, shares, stores, and protects information when you use our services.`,
          sections: PRIVACY_SECTIONS
        },
        dataDeletion: {
          title: 'User Data Deletion',
          updated: `Last updated: ${EFFECTIVE_DATE}`,
          intro: `This page explains how to delete your ${APP_NAME} account and the personal data linked to it, what is removed, what we must keep, and how long each step takes.`,
          sections: DATA_DELETION_SECTIONS
        },
        about: {
          title: 'About Us',
          updated: `${APP_NAME} official information`,
          intro: '',
          sections: ABOUT_SECTIONS
        }
      }
    }
  },
  computed: {
    activePage() {
      if (this.currentPath === '/recharge-pages/') return 'recharge'
      if (this.currentPath === '/Terms') return 'terms'
      if (this.currentPath === '/Privacy') return 'privacy'
      if (this.currentPath === '/DataDeletion') return 'dataDeletion'
      if (this.currentPath === '/About') return 'about'
      return 'home'
    },
    pageConfig() {
      return this.pageMap[this.activePage] || this.pageMap.about
    }
  },
  watch: {
    currentPath() {
      this.updateDocumentTitle()
    }
  },
  mounted() {
    this.syncPath()
    this.updateDocumentTitle()
    window.addEventListener('popstate', this.syncPath)
    this.loadAndroidRelease()
    this.loadStoreLinks()
  },
  beforeUnmount() {
    window.removeEventListener('popstate', this.syncPath)
  },
  methods: {
    normalizePath(path) {
      // The site is served as static files without an nginx history fallback, so every
      // pushState route also ships as its own <route>/index.html. That means a direct hit
      // arrives with a trailing slash, and store reviewers often retype links in a
      // different case - both must land on the same page instead of falling back home.
      const trimmed = path.length > 1 ? path.replace(/\/+$/, '') : path
      if (trimmed.toLowerCase() === '/recharge-pages') return '/recharge-pages/'
      const known = ['/Terms', '/Privacy', '/DataDeletion', '/About', '/index']
      const match = known.find(item => item.toLowerCase() === trimmed.toLowerCase().replace(/-/g, ''))
      return match || '/index'
    },
    syncPath() {
      this.currentPath = this.normalizePath(window.location.pathname)
      this.menuOpen = false
    },
    navigate(path) {
      const normalizedPath = this.normalizePath(path)
      if (window.location.pathname !== normalizedPath) {
        window.history.pushState({}, '', normalizedPath)
      }
      this.currentPath = normalizedPath
      this.menuOpen = false
      window.scrollTo({ top: 0, behavior: 'smooth' })
    },
    updateDocumentTitle() {
      const suffix = this.activePage === 'home'
        ? 'Official Website'
        : this.activePage === 'recharge'
          ? 'Recharge Coins'
          : this.pageConfig.title
      document.title = `${APP_NAME} - ${suffix}`
    },
    async loadAndroidRelease() {
      // A null result just hides the direct APK line; the site must not fail on it.
      this.androidRelease = await getLatestApk()
    },
    async loadStoreLinks() {
      const links = await getStoreLinks()
      if (links.googlePlayUrl) this.googlePlayUrl = links.googlePlayUrl
      if (links.appStoreUrl) this.appStoreUrl = links.appStoreUrl
      this.storePreviews = this.storePreviews.map(preview => ({
        ...preview,
        url: preview.platform === 'App Store' ? this.appStoreUrl : this.googlePlayUrl
      }))
    },
    trackApkDownload() {
      // The download endpoint counts the hit server-side; refresh the shown build afterwards so a
      // release published while the visitor sits on the page is picked up.
      window.setTimeout(() => this.loadAndroidRelease(), 3000)
    },
    closePopup() {
      this.popup.visible = false
    }
  }
}
</script>

<style>
:root {
  --color-ink: #26152f;
  --color-muted: #765d84;
  --color-deep: #6b1fb1;
  --color-purple: #9b45ff;
  --color-pink: #f35aa5;
  --color-hot: #ff3f91;
  --color-rose: #fff0f8;
  --color-lavender: #f4e8ff;
  --color-paper: #fff8fc;
  --color-white: #ffffff;
  --shadow-soft: 0 18px 42px rgba(112, 33, 138, 0.16);
}

.download-meta {
  margin: 12px 0 0;
  font-size: 14px;
  color: var(--color-muted);
}

a.primary-action,
a.secondary-action,
a.store-download {
  cursor: pointer;
  text-decoration: none;
}

* {
  box-sizing: border-box;
}

html {
  background: var(--color-paper);
}

body {
  margin: 0;
  color: var(--color-ink);
  background: var(--color-paper);
  font-family: Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
}

button,
a {
  font: inherit;
}

a {
  color: inherit;
}

button {
  cursor: pointer;
}

.site-shell {
  min-height: 100vh;
  overflow-x: hidden;
}

.site-header {
  position: sticky;
  top: 0;
  z-index: 20;
  height: 84px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 28px;
  padding: 0 7vw;
  background: rgba(255, 248, 252, 0.9);
  border-bottom: 1px solid rgba(131, 57, 154, 0.12);
  backdrop-filter: blur(14px);
}

.brand {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  text-decoration: none;
  flex: 0 0 auto;
}

.brand-logo {
  width: 46px;
  height: 46px;
  border-radius: 13px;
  object-fit: cover;
  display: block;
  box-shadow: 0 6px 16px rgba(60, 30, 10, 0.18);
}

.brand-name {
  font-size: 1.45rem;
  font-weight: 800;
  letter-spacing: 0.06em;
  color: #83399a;
  line-height: 1;
}

.desktop-nav {
  display: flex;
  align-items: center;
  gap: 10px;
}

.desktop-nav a,
.mobile-nav a {
  min-height: 40px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0 16px;
  border-radius: 8px;
  color: var(--color-muted);
  text-decoration: none;
  font-weight: 800;
}

.desktop-nav a.active,
.desktop-nav a:hover,
.mobile-nav a.active,
.mobile-nav a:hover {
  color: var(--color-deep);
  background: rgba(243, 90, 165, 0.14);
}

.menu-button {
  display: none;
  width: 44px;
  height: 44px;
  padding: 0;
  border: 1px solid rgba(131, 57, 154, 0.16);
  border-radius: 8px;
  background: var(--color-white);
}

.menu-button span {
  display: block;
  width: 18px;
  height: 2px;
  margin: 4px auto;
  background: var(--color-ink);
}

.mobile-nav {
  position: absolute;
  top: 72px;
  left: 20px;
  right: 20px;
  display: grid;
  gap: 6px;
  padding: 10px;
  background: var(--color-white);
  border: 1px solid rgba(131, 57, 154, 0.12);
  border-radius: 8px;
  box-shadow: var(--shadow-soft);
}

.hero {
  position: relative;
  min-height: 76vh;
  display: flex;
  align-items: center;
  overflow: hidden;
  background: linear-gradient(110deg, #ffe6f2 0%, #f5d6ff 48%, #b55cff 100%);
}

.hero::before {
  content: "";
  position: absolute;
  inset: 0;
  z-index: 1;
  background: linear-gradient(90deg, rgba(255, 248, 252, 0.92) 0%, rgba(255, 248, 252, 0.68) 34%, rgba(255, 248, 252, 0.04) 64%);
}

.hero-image {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
}

.hero-copy {
  position: relative;
  z-index: 2;
  width: min(560px, 88vw);
  margin-left: 7vw;
  padding: 54px 0;
}

.hero-logo {
  width: 112px;
  height: 112px;
  border-radius: 30px;
  object-fit: cover;
  display: block;
  margin-bottom: 16px;
  box-shadow: 0 16px 34px rgba(60, 30, 10, 0.24);
}

.eyebrow,
.section-kicker,
.document-kicker {
  margin: 0 0 14px;
  color: var(--color-hot);
  font-size: 0.86rem;
  font-weight: 900;
  text-transform: uppercase;
}

.hero h1 {
  margin: 0;
  color: var(--color-ink);
  font-size: 4.25rem;
  line-height: 1;
  font-weight: 900;
}

.hero-text {
  max-width: 500px;
  margin: 22px 0 0;
  color: #3c2449;
  font-size: 1.22rem;
  line-height: 1.58;
}

.hero-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 32px;
}

.primary-action,
.secondary-action,
.store-card button,
.store-card .store-download {
  min-height: 52px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  border: 0;
  border-radius: 8px;
  font-weight: 900;
}

.primary-action {
  min-width: 170px;
  color: var(--color-white);
  background: linear-gradient(135deg, var(--color-purple), var(--color-pink));
  box-shadow: 0 14px 30px rgba(154, 69, 255, 0.3);
}

.secondary-action {
  min-width: 170px;
  color: var(--color-deep);
  background: var(--color-white);
  border: 1px solid rgba(131, 57, 154, 0.16);
}

.action-icon {
  position: relative;
  width: 22px;
  height: 22px;
  flex: 0 0 22px;
  display: inline-block;
}

.play-icon::before {
  content: "";
  position: absolute;
  left: 5px;
  top: 3px;
  border-top: 8px solid transparent;
  border-bottom: 8px solid transparent;
  border-left: 12px solid currentColor;
}

.phone-icon {
  border: 2px solid currentColor;
  border-radius: 7px;
}

.phone-icon::after {
  content: "";
  position: absolute;
  left: 7px;
  right: 7px;
  bottom: 3px;
  height: 2px;
  background: currentColor;
}

.section {
  padding: 72px 7vw;
}

.section-inner {
  width: min(1120px, 100%);
  margin: 0 auto;
}

.two-column {
  display: grid;
  grid-template-columns: minmax(0, 0.9fr) minmax(0, 1.1fr);
  gap: 48px;
  align-items: start;
}

.section h2 {
  margin: 0;
  font-size: 2.25rem;
  line-height: 1.16;
}

.section-lede,
.section-heading p {
  margin: 0;
  color: var(--color-muted);
  font-size: 1.05rem;
  line-height: 1.8;
}

.section-lede p {
  margin: 0 0 12px;
}

.section-lede a {
  color: var(--color-deep);
  font-weight: 600;
}

.section-heading {
  width: min(760px, 100%);
  margin-bottom: 32px;
}

.section-heading h2 {
  margin-bottom: 14px;
}

.feature-section {
  background: var(--color-white);
}

.feature-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 18px;
}

.feature-card {
  min-height: 244px;
  padding: 28px;
  border: 1px solid rgba(131, 57, 154, 0.12);
  border-radius: 8px;
  background: linear-gradient(180deg, #ffffff 0%, #fff2fa 100%);
  box-shadow: 0 10px 24px rgba(131, 57, 154, 0.08);
}

.feature-card h3,
.experience-item h3,
.phone-shot h3,
.store-card h3 {
  margin: 18px 0 10px;
  font-size: 1.2rem;
}

.feature-card p,
.experience-item p,
.phone-shot p {
  margin: 0;
  color: var(--color-muted);
  line-height: 1.65;
}

.feature-icon {
  position: relative;
  width: 44px;
  height: 44px;
  display: inline-block;
  border-radius: 8px;
  background: linear-gradient(135deg, #f768b2, #9a45ff);
}

.feature-icon::before,
.feature-icon::after {
  content: "";
  position: absolute;
}

.icon-route::before {
  left: 12px;
  top: 16px;
  width: 20px;
  height: 10px;
  border-top: 3px solid var(--color-white);
  border-right: 3px solid var(--color-white);
  border-radius: 12px;
  transform: rotate(-18deg);
}

.icon-route::after {
  right: 9px;
  bottom: 10px;
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: var(--color-white);
}

.icon-paw::before {
  left: 15px;
  top: 18px;
  width: 14px;
  height: 12px;
  border-radius: 50% 50% 45% 45%;
  background: var(--color-white);
}

.icon-paw::after {
  left: 10px;
  top: 10px;
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: var(--color-white);
  box-shadow: 10px -3px 0 var(--color-white), 20px 0 0 var(--color-white);
}

.icon-ai::before {
  left: 12px;
  top: 11px;
  width: 20px;
  height: 20px;
  border: 3px solid var(--color-white);
  border-radius: 50%;
}

.icon-ai::after {
  left: 21px;
  top: 6px;
  width: 3px;
  height: 32px;
  background: var(--color-white);
  transform: rotate(45deg);
}

.store-section {
  background: linear-gradient(180deg, #fff8fc 0%, #f9ecff 100%);
}

.store-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 22px;
}

.store-card {
  overflow: hidden;
  border: 1px solid rgba(131, 57, 154, 0.12);
  border-radius: 8px;
  background: var(--color-white);
  box-shadow: var(--shadow-soft);
}

.store-card img {
  display: block;
  width: 100%;
  aspect-ratio: 16 / 9;
  object-fit: cover;
}

.store-card div {
  padding: 22px;
}

.store-card div > p {
  margin: 0;
  color: var(--color-hot);
  font-weight: 900;
}

.store-card button,
.store-card .store-download {
  min-height: 44px;
  margin-top: 12px;
  padding: 0 16px;
  color: var(--color-white);
  background: linear-gradient(135deg, var(--color-purple), var(--color-pink));
}

.screenshot-section {
  background: var(--color-white);
}

.phone-gallery {
  display: grid;
  grid-template-columns: repeat(6, minmax(160px, 1fr));
  gap: 16px;
  overflow-x: auto;
  padding-bottom: 10px;
}

.phone-shot {
  min-width: 160px;
  padding: 12px;
  border: 1px solid rgba(131, 57, 154, 0.12);
  border-radius: 8px;
  background: #fff8fc;
}

.phone-shot img {
  display: block;
  width: 100%;
  aspect-ratio: 9 / 20;
  object-fit: cover;
  border-radius: 8px;
  box-shadow: 0 14px 28px rgba(81, 29, 111, 0.14);
}

.experience-section {
  background: #fbebff;
}

.experience-list {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 28px;
  margin-top: 34px;
}

.experience-item {
  display: grid;
  grid-template-columns: 58px minmax(0, 1fr);
  gap: 18px;
  align-items: start;
}

.experience-item > span {
  width: 58px;
  height: 58px;
  display: grid;
  place-items: center;
  border-radius: 8px;
  color: var(--color-white);
  background: linear-gradient(135deg, var(--color-purple), var(--color-pink));
  font-weight: 900;
}

.document-page {
  padding: 68px 7vw 86px;
  background: linear-gradient(180deg, #fff1f8 0%, #f4e8ff 100%);
}

.document-shell {
  width: min(920px, 100%);
  margin: 0 auto;
  padding: 42px;
  background: rgba(255, 255, 255, 0.94);
  border: 1px solid rgba(131, 57, 154, 0.12);
  border-radius: 8px;
  box-shadow: var(--shadow-soft);
}

.back-button {
  min-height: 40px;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 0 14px;
  color: var(--color-deep);
  background: rgba(243, 90, 165, 0.12);
  border: 0;
  border-radius: 8px;
  font-weight: 900;
}

.back-icon {
  width: 9px;
  height: 9px;
  border-left: 2px solid currentColor;
  border-bottom: 2px solid currentColor;
  transform: rotate(45deg);
}

.document-kicker {
  margin-top: 34px;
}

.document-shell h1 {
  margin: 0;
  font-size: 2.5rem;
  line-height: 1.16;
}

.updated,
.document-intro {
  color: var(--color-muted);
}

.document-intro {
  margin: 18px 0 34px;
  font-size: 1.05rem;
  line-height: 1.75;
}

.legal-section {
  padding-top: 26px;
  border-top: 1px solid rgba(131, 57, 154, 0.12);
}

.legal-section h2,
.contact-details h2 {
  margin: 0 0 12px;
  font-size: 1.15rem;
}

.legal-section p,
.contact-details p {
  margin: 0 0 14px;
  color: var(--color-muted);
  line-height: 1.75;
  word-break: break-word;
}

.contact-details {
  margin-top: 26px;
  padding-top: 26px;
  border-top: 2px solid rgba(154, 69, 255, 0.2);
}

.contact-details a {
  color: var(--color-deep);
  font-weight: 900;
}

.site-footer {
  padding: 34px 7vw;
  color: rgba(255, 255, 255, 0.86);
  background: #311342;
}

.footer-inner {
  width: min(1120px, 100%);
  margin: 0 auto;
  display: flex;
  justify-content: space-between;
  gap: 32px;
}

.footer-inner p {
  max-width: 560px;
  margin: 8px 0 0;
  line-height: 1.55;
}

.footer-brand {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 10px;
}

.footer-logo {
  width: 44px;
  height: 44px;
  border-radius: 13px;
  object-fit: cover;
  display: block;
  filter: drop-shadow(0 8px 18px rgba(0, 0, 0, 0.22));
}

.footer-name {
  font-size: 1.3rem;
  font-weight: 800;
  letter-spacing: 0.06em;
  line-height: 1;
}

.footer-links {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 12px 18px;
}

.footer-links a {
  color: var(--color-white);
  font-weight: 900;
  text-decoration: none;
}

.modal-overlay {
  position: fixed;
  inset: 0;
  z-index: 40;
  display: grid;
  place-items: center;
  padding: 20px;
  background: rgba(38, 21, 47, 0.54);
}

.modal {
  width: min(360px, 100%);
  padding: 28px;
  border-radius: 8px;
  background: var(--color-white);
  box-shadow: 0 24px 56px rgba(38, 21, 47, 0.24);
}

.modal h2 {
  margin: 0 0 10px;
  font-size: 1.35rem;
}

.modal p {
  margin: 0 0 24px;
  color: var(--color-muted);
  line-height: 1.55;
}

.modal button {
  min-height: 44px;
  width: 100%;
  color: var(--color-white);
  background: linear-gradient(135deg, var(--color-purple), var(--color-pink));
  border: 0;
  border-radius: 8px;
  font-weight: 900;
}

@media (max-width: 1000px) {
  .phone-gallery {
    grid-template-columns: repeat(3, minmax(170px, 1fr));
  }
}

@media (max-width: 900px) {
  .site-header {
    height: 72px;
    padding: 0 20px;
  }

  .brand-logo {
    width: 40px;
    height: 40px;
    border-radius: 12px;
  }

  .brand-name {
    font-size: 1.25rem;
  }

  .desktop-nav {
    display: none;
  }

  .menu-button {
    display: block;
  }

  .hero {
    min-height: 78vh;
    align-items: flex-start;
  }

  .hero::before {
    background: linear-gradient(180deg, rgba(255, 248, 252, 0.94) 0%, rgba(255, 248, 252, 0.76) 46%, rgba(255, 248, 252, 0.08) 100%);
  }

  .hero-image {
    opacity: 0.72;
    object-position: 55% center;
  }

  .hero-copy {
    width: min(500px, calc(100vw - 40px));
    margin: 0 auto;
    padding-top: 46px;
  }

  .hero h1 {
    font-size: 3.1rem;
  }

  .hero-text {
    font-size: 1.08rem;
  }

  .two-column,
  .feature-grid,
  .store-grid,
  .experience-list {
    grid-template-columns: 1fr;
  }

  .section {
    padding: 56px 20px;
  }

  .section h2 {
    font-size: 1.9rem;
  }

  .footer-inner {
    flex-direction: column;
  }

  .footer-links {
    justify-content: flex-start;
  }
}

@media (max-width: 560px) {
  .hero {
    min-height: 84vh;
  }

  .hero-logo {
    width: 92px;
    height: 92px;
    border-radius: 25px;
  }

  .hero h1 {
    font-size: 2.7rem;
  }

  .hero-actions,
  .primary-action,
  .secondary-action {
    width: 100%;
  }

  .feature-card {
    min-height: 0;
    padding: 24px;
  }

  .phone-gallery {
    grid-template-columns: repeat(6, minmax(158px, 1fr));
  }

  .experience-item {
    grid-template-columns: 48px minmax(0, 1fr);
  }

  .experience-item > span {
    width: 48px;
    height: 48px;
  }

  .document-page {
    padding: 36px 14px 56px;
  }

  .document-shell {
    padding: 24px;
  }

  .document-shell h1 {
    font-size: 2rem;
  }
}
</style>
